import { createContext, useMemo, useState } from 'react';

import type { ChartOption } from './blocks/Chart';

export type SeriesFocus = {
  hidden: ReadonlySet<string>;
  hovered?: string;
  hover: (name?: string) => void;
  toggle: (name: string) => void;
  isolate: (name: string, names: Array<string>) => void;
  showAll: () => void;
};

const DIM = 0.2;

export const SeriesFocusContext = createContext<SeriesFocus | undefined>(undefined);

export function useSeriesFocus(): SeriesFocus {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [hovered, setHovered] = useState<string>();
  return useMemo(
    () => ({
      hidden,
      hovered,
      hover: setHovered,
      toggle: (name: string) => setHidden((current) => (current.has(name) ? new Set([...current].filter((entry) => entry !== name)) : new Set([...current, name]))),
      isolate: (name: string, names: Array<string>) =>
        setHidden((current) => {
          const others = names.filter((entry) => entry !== name);
          const isolated = !current.has(name) && others.every((entry) => current.has(entry));
          return isolated ? new Set() : new Set(others);
        }),
      showAll: () => setHidden(new Set()),
    }),
    [hidden, hovered],
  );
}

type Series = Record<string, unknown> & { name?: string };
type Styled = Record<string, unknown> | undefined;

export const seriesOf = (name: string | undefined) => (name ?? '').replace(/ so far$/, '');
const faded = (style: Styled, factor: number) => ({ ...style, opacity: Number(style?.opacity ?? 1) * factor });

function withOpacity(series: Series, factor: number): Series {
  const mark = series.markPoint as { itemStyle?: Styled; label?: Styled } | undefined;
  return {
    ...series,
    lineStyle: faded(series.lineStyle as Styled, factor),
    itemStyle: faded(series.itemStyle as Styled, factor),
    ...(series.areaStyle ? { areaStyle: faded(series.areaStyle as Styled, factor) } : {}),
    ...(series.endLabel ? { endLabel: faded(series.endLabel as Styled, factor) } : {}),
    ...(series.label ? { label: faded(series.label as Styled, factor) } : {}),
    ...(mark ? { markPoint: { ...mark, itemStyle: faded(mark.itemStyle, factor), label: faded(mark.label, factor) } } : {}),
  };
}

export function focusOption(option: ChartOption, focus: SeriesFocus | undefined): ChartOption {
  const series = option.series as Array<Series> | undefined;
  if (!focus || !series) return option;
  const hovered = focus.hovered && !focus.hidden.has(focus.hovered) && series.some((entry) => seriesOf(entry.name) === focus.hovered) ? focus.hovered : undefined;
  return {
    ...option,
    series: series.filter((entry) => !focus.hidden.has(seriesOf(entry.name))).map((entry) => withOpacity(entry, hovered && seriesOf(entry.name) !== hovered ? DIM : 1)),
  };
}
